import React, { useState } from 'react';
import { Alert, Box, Typography, useMediaQuery, IconButton, Collapse, Tooltip, Link } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import round from '../../../helpers/round';
import CvLLUsNavyGraphSvg from './CvLLUsNavyGraphSvg';

const CvVsLLUsNavyResult = ({ result }) => {
  const { liquidLimit } = result.inputs;
  const {
    canUserSeeCalculationSteps,
    cv1Limits,
    cv2Limits,
    cv3Limits,
    isCv1LimitOK,
    isCv2LimitOK,
    isCv3LimitOK,
    cv1_m2perYear,
    cv2_m2perYear,
    cv3_m2perYear,
    cv1_cm2PerSecond,
    cv2_cm2PerSecond,
    cv3_cm2PerSecond,
  } = result.outputs;

  const isSmallScreen = useMediaQuery('(max-width:700px)');
  const [showCv1Details, setShowCv1Details] = useState(false);
  const [showCv2Details, setShowCv2Details] = useState(false);
  const [showCv3Details, setShowCv3Details] = useState(false);

  const GraphLimitAlert = ({ type }) => {
    return (
      <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Liquid Limit (LL) provided ({round(liquidLimit, 2)}%) is out of the limits of the graph.{' '}
          {type === 'cv1' && 'For Cv-1, valid LL(%) values range between 33-120.'}
          {type === 'cv2' && 'For Cv-2 valid LL(%) values range between 27-160.'}
          {type === 'cv3' && 'For Cv-3 valid LL(%) values range between 27-120.'}
        </Typography>
      </Alert>
    );
  };

  return (
    <section className="output cvLLUsNavyOutput">
      <h2>Output </h2>
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            LL vs c<sub>v</sub> Relationship (US Navy 1982)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, textAlign: 'justify', mb: 2 }}>
            The US Navy (1982) chart relates the liquid limit (LL) of clay to the coefficient of consolidation (c
            <sub>v</sub>) using three empirical curves. The chart below is used to read c<sub>v</sub> values at the provided LL
            input.
          </Typography>
          <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto', mb: 2 }}>
            <CvLLUsNavyGraphSvg
              ll={liquidLimit}
              showOverlay
              cvValues={[
                {
                  key: 'cv1',
                  show: isCv1LimitOK,
                  cvM2PerYear: cv1_m2perYear,
                  cvCm2PerSecond: cv1_cm2PerSecond,
                },
                {
                  key: 'cv2',
                  show: isCv2LimitOK,
                  cvM2PerYear: cv2_m2perYear,
                  cvCm2PerSecond: cv2_cm2PerSecond,
                },
                {
                  key: 'cv3',
                  show: isCv3LimitOK,
                  cvM2PerYear: cv3_m2perYear,
                  cvCm2PerSecond: cv3_cm2PerSecond,
                },
              ]}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {isCv1LimitOK ? (
              <Alert
                icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
                severity="success"
                variant="outlined"
                sx={{
                  backgroundColor: 'rgba(46,125,50,0.04)',
                  alignItems: 'flex-start',
                  '& .MuiAlert-icon': { alignSelf: 'flex-start', mt: 0.25, minWidth: 24 },
                  '& .MuiAlert-action': { alignSelf: 'flex-start', mt: 0.25, width: 32, justifyContent: 'center', p: 0, ml: 1 },
                  '& .MuiAlert-message': { width: '100%' },
                }}
                action={
                  canUserSeeCalculationSteps ? (
                    <IconButton
                      size="small"
                      color="inherit"
                      onClick={() => setShowCv1Details((v) => !v)}
                      sx={{ width: 32, height: 32 }}
                    >
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{
                          transform: showCv1Details ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.15s',
                        }}
                      />
                    </IconButton>
                  ) : (
                    <Tooltip
                      title={
                        <span>
                          Upgrade to unlock calculation details.{' '}
                          <Link
                            href="/pricing"
                            target="_blank"
                            rel="noopener"
                            style={{ color: '#90caf9', textDecoration: 'underline' }}
                          >
                            See plans
                          </Link>
                        </span>
                      }
                      leaveDelay={500}
                      arrow
                    >
                      <LockOutlinedIcon
                        sx={{
                          fontSize: 20,
                          color: 'text.disabled',
                          cursor: 'not-allowed',
                          mr: 1,
                          mt: 0.5,
                        }}
                      />
                    </Tooltip>
                  )
                }
              >
                <Typography variant="body2" color="text.secondary">
                  <b>
                    c<sub>v-1</sub>
                  </b>{' '}
                  = {round(cv1_m2perYear, 2)} m<sup>2</sup>/year
                </Typography>
                {canUserSeeCalculationSteps && (
                  <Collapse in={showCv1Details} unmountOnExit>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      For liquid limit = {round(liquidLimit, 2)}%,
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      c<sub>v-1</sub> = {round(cv1_cm2PerSecond, 6)} cm<sup>2</sup>/s (from left-side scale)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      c<sub>v-1</sub> = {round(cv1_m2perYear, 2)} m<sup>2</sup>/year (from right-side scale)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Note: Valid range is LL = {cv1Limits.lowerLimit}%–{cv1Limits.upperLimit}%.
                    </Typography>
                  </Collapse>
                )}
              </Alert>
            ) : (
              <GraphLimitAlert type="cv1" />
            )}

            {isCv2LimitOK ? (
              <Alert
                icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
                severity="success"
                variant="outlined"
                sx={{
                  backgroundColor: 'rgba(46,125,50,0.04)',
                  alignItems: 'flex-start',
                  '& .MuiAlert-icon': { alignSelf: 'flex-start', mt: 0.25, minWidth: 24 },
                  '& .MuiAlert-action': { alignSelf: 'flex-start', mt: 0.25, width: 32, justifyContent: 'center', p: 0, ml: 1 },
                  '& .MuiAlert-message': { width: '100%' },
                }}
                action={
                  canUserSeeCalculationSteps ? (
                    <IconButton
                      size="small"
                      color="inherit"
                      onClick={() => setShowCv2Details((v) => !v)}
                      sx={{ width: 32, height: 32 }}
                    >
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{
                          transform: showCv2Details ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.15s',
                        }}
                      />
                    </IconButton>
                  ) : (
                    <Tooltip
                      title={
                        <span>
                          Upgrade to unlock calculation details.{' '}
                          <Link
                            href="/pricing"
                            target="_blank"
                            rel="noopener"
                            style={{ color: '#90caf9', textDecoration: 'underline' }}
                          >
                            See plans
                          </Link>
                        </span>
                      }
                      leaveDelay={500}
                      arrow
                    >
                      <LockOutlinedIcon
                        sx={{
                          fontSize: 20,
                          color: 'text.disabled',
                          cursor: 'not-allowed',
                          mr: 1,
                          mt: 0.5,
                        }}
                      />
                    </Tooltip>
                  )
                }
              >
                <Typography variant="body2" color="text.secondary">
                  <b>
                    c<sub>v-2</sub>
                  </b>{' '}
                  = {round(cv2_m2perYear, 2)} m<sup>2</sup>/year
                </Typography>
                {canUserSeeCalculationSteps && (
                  <Collapse in={showCv2Details} unmountOnExit>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      For liquid limit = {round(liquidLimit, 2)}%,
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      c<sub>v-2</sub> = {round(cv2_cm2PerSecond, 6)} cm<sup>2</sup>/s (from left-side scale)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      c<sub>v-2</sub> = {round(cv2_m2perYear, 2)} m<sup>2</sup>/year (from right-side scale)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Note: Valid range is LL = {cv2Limits.lowerLimit}%–{cv2Limits.upperLimit}%.
                    </Typography>
                  </Collapse>
                )}
              </Alert>
            ) : (
              <GraphLimitAlert type="cv2" />
            )}

            {isCv3LimitOK ? (
              <Alert
                icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
                severity="success"
                variant="outlined"
                sx={{
                  backgroundColor: 'rgba(46,125,50,0.04)',
                  alignItems: 'flex-start',
                  '& .MuiAlert-icon': { alignSelf: 'flex-start', mt: 0.25, minWidth: 24 },
                  '& .MuiAlert-action': { alignSelf: 'flex-start', mt: 0.25, width: 32, justifyContent: 'center', p: 0, ml: 1 },
                  '& .MuiAlert-message': { width: '100%' },
                }}
                action={
                  canUserSeeCalculationSteps ? (
                    <IconButton
                      size="small"
                      color="inherit"
                      onClick={() => setShowCv3Details((v) => !v)}
                      sx={{ width: 32, height: 32 }}
                    >
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{
                          transform: showCv3Details ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.15s',
                        }}
                      />
                    </IconButton>
                  ) : (
                    <Tooltip
                      title={
                        <span>
                          Upgrade to unlock calculation details.{' '}
                          <Link
                            href="/pricing"
                            target="_blank"
                            rel="noopener"
                            style={{ color: '#90caf9', textDecoration: 'underline' }}
                          >
                            See plans
                          </Link>
                        </span>
                      }
                      leaveDelay={500}
                      arrow
                    >
                      <LockOutlinedIcon
                        sx={{
                          fontSize: 20,
                          color: 'text.disabled',
                          cursor: 'not-allowed',
                          mr: 1,
                          mt: 0.5,
                        }}
                      />
                    </Tooltip>
                  )
                }
              >
                <Typography variant="body2" color="text.secondary">
                  <b>
                    c<sub>v-3</sub>
                  </b>{' '}
                  = {round(cv3_m2perYear, 2)} m<sup>2</sup>/year
                </Typography>
                {canUserSeeCalculationSteps && (
                  <Collapse in={showCv3Details} unmountOnExit>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      For liquid limit = {round(liquidLimit, 2)}%,
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      c<sub>v-3</sub> = {round(cv3_cm2PerSecond, 6)} cm<sup>2</sup>/s (from left-side scale)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      c<sub>v-3</sub> = {round(cv3_m2perYear, 2)} m<sup>2</sup>/year (from right-side scale)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Note: Valid range is LL = {cv3Limits.lowerLimit}%–{cv3Limits.upperLimit}%.
                    </Typography>
                  </Collapse>
                )}
              </Alert>
            ) : (
              <GraphLimitAlert type="cv3" />
            )}
          </Box>
        </Alert>
      </Box>
    </section>
  );
};

export default React.memo(CvVsLLUsNavyResult);
